import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Form } from "antd";
import {
  CheckCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  GiftFilled,
  PlusOutlined,
} from "@ant-design/icons";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import dayjs from "dayjs";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import CommonModal from "../../../global/components/CommonModal";
import ConfirmModal from "../../../global/components/ConfirmModal";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { PageNotFound } from "../../../global/components/PageNotFound";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import { getDealerNo } from "../../../global/hooks/getUserRole";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import { getOptionLabel, termOptions } from "../../../utils/Common";
import {
  calcEndTime,
  formatDateTime,
  getEditColumns,
  getRowKey,
  isLifetimeTerm,
  PrepurchasedItemsForm,
  renewalFromApi,
  useDealerRenewal,
} from "./PrepurchasedItemsForm";

const isBlank = (value) => value == null || value === "";

/** start_time / end_time / renewal for one picked row, from the inline edits. */
export const getPrepurchasedRowSchedule = (row, edit = {}, showRenewal) => {
  const isLifetime = isLifetimeTerm(row?.Term);
  const start = edit.StartTime;
  const schedule = {
    renewal: showRenewal && edit.Renewal ? 1 : 0,
  };
  if (start) schedule.start_time = formatDateTime(start);
  if (!isLifetime && start) {
    schedule.end_time = formatDateTime(calcEndTime(start, row?.Term));
  }
  return schedule;
};

/**
 * Empty list → Add popup (pick pre-purchased rows) → set Start Time / Renewal → Apply.
 * onApply receives the picked rows and their edits once Start Time is valid.
 */
export const PrePurchaseLicensePanel = ({
  breadcrumbItems,
  cancelPath,
  dealerNo,
  loading,
  applying,
  onApply,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const pickerRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const showRenewal = useDealerRenewal(); 
  const onAddRows = () => {
    const picked = pickerRef.current?.getSelectedRows() ?? [];
    if (!picked.length) {
      openErrorNotification("Please select at least one item.");
      return;
    }
    const now = dayjs();
    const edits = { ...(form.getFieldValue("edits") || {}) };
    picked.forEach((row) => {
      edits[String(getRowKey(row))] = {
        StartTime: now,
        EndTime: calcEndTime(now, row?.Term),
        Renewal: showRenewal && renewalFromApi(row),
      };
    });
    form.setFieldsValue({ edits });
    setRows((prev) => [
      ...prev,
      ...picked.map((row) => ({ ...row, key: getRowKey(row) })),
    ]);
    setShowAddModal(false);
  };

  const onDeleteRows = () => {
    setRows((prev) => prev.filter((row) => !checkedKeys.includes(row.key)));
    setCheckedKeys([]);
  };

  const handleApply = async () => {
    if (!rows.length) {
      openErrorNotification("Please add at least one item.");
      return;
    }
    try {
      await form.validateFields(
        rows.map((row) => ["edits", String(row.key), "StartTime"]),
      );
    } catch {
      return;
    }
    if (isBlank(dealerNo)) {
      openErrorNotification("Dealer is required.");
      return;
    }
    const edits = form.getFieldValue("edits") || {};
    onApply(rows, (row) =>
      getPrepurchasedRowSchedule(row, edits[String(row.key)], showRenewal),
    );
  };

  const columns = [
    {
      title: "No",
      key: "No",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Item",
      key: "ItemName",
      width: 140,
      ellipsis: true,
      render: (_, record) => record?.ItemName || record?.Item || "",
    },
    {
      title: "Code",
      key: "CodeName",
      width: 130,
      ellipsis: true,
      render: (_, record) => record?.CodeName || record?.Code || "",
    },
    {
      title: "Term",
      dataIndex: "Term",
      key: "Term",
      width: 100,
      render: (value) => getOptionLabel(termOptions, value) || value || "",
    },
    ...getEditColumns({
      selectedKeys: rows.map((row) => row.key),
      form,
      showRenewal,
    }),
  ];

  const buttonsData = [
    {
      label: "Apply",
      icon: <CheckCircleOutlined />,
      onClick: handleApply,
      loading: applying,
      disabled: applying || loading || !rows.length,
    },
    {
      label: "Cancel",
      icon: <CloseOutlined />,
      onClick: () => navigate(cancelPath),
    },
  ];

  const itemButtonsData = [
    {
      label: "Add",
      icon: <PlusOutlined />,
      onClick: () => setShowAddModal(true),
      disabled: loading || isBlank(dealerNo),
    },
    {
      label: "Delete",
      icon: <DeleteOutlined />,
      onClick: () => setShowDeleteModal(true),
      disabled: !checkedKeys.length,
    },
  ];

  return (
    <div>
      <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
          <CommonButton buttonsData={buttonsData} />
        </CommonBreadcrumb>
      </div>

      <div className="bg-white overflow-hidden border rounded-[4px]">
        <div className="flex items-center justify-between bg-gray-200 px-2.5 py-1.5">
          <div className="flex items-center">
            <GiftFilled className="text-[16px] text-black" />
            <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
              Pre-Purchased License
            </span>
          </div>
          <div className="-mt-2 -mr-2">
            <CommonButton buttonsData={itemButtonsData} />
          </div>
        </div>
        <div className="px-2 pt-3 pb-3">
          <Form
            form={form}
            layout="vertical"
            className="prepurchased-items-form"
          >
            <GlobalTable
              columns={columns}
              dataSource={rows}
              pagination={false}
              rowSelection={{
                selectedRowKeys: checkedKeys,
                onChange: setCheckedKeys,
              }}
            />
          </Form>
        </div>
      </div>

      <ConfirmModal
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        onDeleteConfirm={onDeleteRows}
        setSelectedRowKeys={setCheckedKeys}
      />

      <CommonModal
        title="Add Pre-Purchased License"
        icon={GiftFilled}
        isVisible={showAddModal}
        onClose={() => setShowAddModal(false)}
        width={1000}
        content={
          <PrepurchasedItemsForm
            ref={pickerRef}
            dealerNo={dealerNo}
            multiSelect
            selectOnly
            excludedRows={rows}
          />
        }
        footerComp={
          <div className="flex justify-center mt-4 gap-4">
            <Button
              type="primary"
              className="mr-2 w-[104px] h-[30px] font-medium"
              onClick={onAddRows}
            >
              <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
              Apply
            </Button>
            <Button
              onClick={() => setShowAddModal(false)}
              className="reset-button w-[104px] h-[30px] font-medium"
            >
              <CloseOutlined className="font-medium" />
              Close
            </Button>
          </div>
        }
      />
    </div>
  );
};

export const PrePurchaseLicenseForm = () => {
  const navigate = useNavigate();
  const { siteNo } = useParams();
  const siteInfoPath = `/site/info/${siteNo}`;

  const siteUrl = !isBlank(siteNo)
    ? `${process.env.REACT_APP_API_URL}/api/sites/${siteNo}/`
    : null;
  const {
    data: siteData,
    loading: siteLoading,
    error: siteError,
  } = useGet(siteUrl);

  const postUrl = `${process.env.REACT_APP_API_URL}/api/devices/pre-purchased-items/`;
  const {
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);

  const dealerNo = siteData?.data?.DealerNo ?? getDealerNo() ?? null;

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [postError]);

  if (
    isBlank(siteNo) ||
    siteError ||
    (!siteLoading && siteData && !siteData?.data)
  ) {
    return <PageNotFound />;
  }

  const onApply = async (rows, getSchedule) => {
    const result = await postAction({
      dealer_no: Number(dealerNo),
      items: rows.map((row) => {
        const item = {
          purchase_no: row.PurchaseNo,
          device_no: Number(siteNo),
          ...getSchedule(row),
        };
        if (!isBlank(row.ItemNo)) item.item_no = row.ItemNo;
        return item;
      }),
    });
    if (result == null) return;
    openConfirmNotification("Success");
    navigate(siteInfoPath);
  };

  return (
    <PrePurchaseLicensePanel
      breadcrumbItems={[
        { label: "Site", href: "/site" },
        { label: "Information", href: siteInfoPath },
        {
          label: "Pre-Purchased License",
          href: `/site/${siteNo}/pre-purchase-license`,
        },
      ]}
      cancelPath={siteInfoPath}
      dealerNo={dealerNo}
      loading={siteLoading}
      applying={postLoading}
      onApply={onApply}
    />
  );
};
